import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { compareHarmony } from '../../client';

const HarmonyResult = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const query = new URLSearchParams(location.search);
  const id1 = query.get('id1');
  const id2 = query.get('id2');

  useEffect(() => {
    if (!id1 || !id2) {
      setError('Two IDs are required for comparison.');
      setLoading(false);
      return;
    }

    const fetchResult = async () => {
      try {
        const result = await compareHarmony(id1, id2);
        setData(result);
      } catch (err) {
        setError(err.message || 'Compatibility analysis failed.');
      } finally {
        setLoading(false);
      }
    };

    fetchResult();
  }, [id1, id2]);

  if (loading) {
    return (
      <div className="harmony-container">
        <div className="harmony-card">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            className="loading-spinner"
          >
            O
          </motion.div>
          <h2 className="title">Analyzing compatibility...</h2>
          <p className="subtitle">Calculating attraction direction, friction risk, and match score.</p>
        </div>
        <style jsx>{`
          .harmony-container {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: var(--mobile-shell-padding-top) var(--mobile-shell-padding-x) var(--mobile-shell-padding-bottom);
            background: radial-gradient(circle at center, #2e1065, #0f172a);
            color: white;
          }
          .harmony-card {
            text-align: center;
            padding: 1.5rem 1rem;
            max-width: var(--mobile-shell-max);
          }
          .loading-spinner {
            font-size: 3rem;
            margin-bottom: 1.2rem;
          }
          .title {
            font-size: 1.6rem;
            margin-bottom: 0.6rem;
          }
          .subtitle {
            color: #cbd5e1;
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="harmony-container">
        <div className="harmony-card error">
          <h1 className="title">Analysis failed</h1>
          <p className="error-desc">{error}</p>
          <button className="start-btn secondary" onClick={() => navigate('/harmony/select')}>
            Try again
          </button>
          <p className="hint">Check that both IDs are valid and that the required source data exists.</p>
        </div>
        <style jsx>{`
          .harmony-container {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: var(--mobile-shell-padding-top) var(--mobile-shell-padding-x) var(--mobile-shell-padding-bottom);
            background: #0f172a;
            color: white;
          }
          .harmony-card {
            background: rgba(255, 0, 0, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            padding: 1.5rem 1rem;
            border-radius: 24px;
            text-align: center;
            max-width: var(--mobile-shell-max);
          }
          .error-desc {
            color: #f87171;
            margin-bottom: 1rem;
            font-size: 1rem;
          }
          .hint {
            font-size: 0.88rem;
            color: #94a3b8;
            margin-top: 1rem;
          }
          .start-btn.secondary {
            width: 100%;
            min-height: var(--mobile-button-height);
            padding: 1rem;
            background: rgba(255, 255, 255, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.1);
            color: white;
            border-radius: 16px;
            cursor: pointer;
          }
        `}</style>
      </div>
    );
  }

  const actionGuide = [];
  if (data.behavior_harmony < 50) {
    actionGuide.push('Slow down conflict responses before trying to settle the issue immediately.');
  }
  if (data.ideal_match < 50) {
    actionGuide.push('Compare the top three relationship priorities before reading too much into the score.');
  }
  if (data.similarity < 50) {
    actionGuide.push('Use difference as role split instead of seeing it only as mismatch.');
  }
  if (actionGuide.length === 0) {
    actionGuide.push('This pair looks stable. Keep the rhythm with short and frequent check-ins.');
  }

  const detailLines = [
    data.similarity >= 70
      ? 'The first exchange rhythm is likely to feel naturally aligned.'
      : 'The early stage may depend more on pacing and response timing than on instant similarity.',
    data.behavior_harmony >= 70
      ? 'Recovery after tension looks stronger than average.'
      : 'This pairing may need clearer rules for how to handle misunderstandings.',
    data.ideal_match >= 70
      ? 'Expectation direction is relatively aligned.'
      : 'Preference gaps should be named early instead of assumed away.'
  ];

  return (
    <div className="harmony-container">
      <div className="harmony-card result-card">
        <div className="result-summary-top">
          <span className="result-badge">Compatibility Result</span>
          <h1 className="title">Compatibility result</h1>
          <p className="result-lead">{data.analysis}</p>
        </div>

        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="score-circle"
        >
          <svg viewBox="0 0 100 100" className="progress-ring">
            <circle className="ring-bg" cx="50" cy="50" r="45" />
            <motion.circle
              className="ring-fill"
              cx="50"
              cy="50"
              r="45"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: data.score / 100 }}
              transition={{ duration: 1.5, ease: 'easeOut' }}
            />
          </svg>
          <div className="score-value">
            <span className="number">{data.score}</span>
            <span className="unit">%</span>
          </div>
        </motion.div>

        <div className="participants-bar">
          <span className="name">{id1}</span>
          <span className="love-icon">&lt;&gt;</span>
          <span className="name">{id2}</span>
        </div>

        <div className="stats-grid">
          <div className="stat-box">
            <label>Similarity</label>
            <div className="val">{data.similarity}%</div>
          </div>
          <div className="stat-box">
            <label>Ideal match</label>
            <div className="val">{data.ideal_match}%</div>
          </div>
          <div className="stat-box">
            <label>Behavior harmony</label>
            <div className="val">{data.behavior_harmony}%</div>
          </div>
        </div>

        <div className="guide-box">
          <h3>Do next</h3>
          {actionGuide.map((guide, idx) => (
            <p key={idx}>- {guide}</p>
          ))}
        </div>

        <div className="deep-box">
          <h3>Read a bit deeper</h3>
          {detailLines.map((line, idx) => (
            <p key={idx}>- {line}</p>
          ))}
        </div>

        <div className="action-buttons">
          <button className="start-btn primary" onClick={() => navigate('/harmony/select')}>Compare another pair</button>
          <button className="start-btn secondary" onClick={() => navigate('/')}>Back to home</button>
        </div>
      </div>

      <style jsx>{`
        .harmony-container {
          min-height: 100vh;
          padding: var(--mobile-shell-padding-top) var(--mobile-shell-padding-x) var(--mobile-shell-padding-bottom);
          display: flex;
          align-items: center;
          justify-content: center;
          background: radial-gradient(circle at 10% 20%, #2e1065 0%, #0f172a 90%);
          color: white;
        }

        .harmony-card {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 30px;
          padding: 1.2rem 1rem;
          max-width: var(--mobile-shell-max);
          width: 100%;
          text-align: center;
        }

        .result-summary-top {
          text-align: left;
          margin-bottom: 1rem;
        }

        .result-badge {
          display: inline-flex;
          align-items: center;
          padding: 0.42rem 0.72rem;
          border-radius: 999px;
          background: rgba(244, 114, 182, 0.12);
          border: 1px solid rgba(244, 114, 182, 0.22);
          color: #fbcfe8;
          font-size: 0.76rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          margin-bottom: 0.75rem;
        }

        .title {
          font-size: var(--mobile-title-md);
          margin: 0 0 0.5rem;
          text-align: left;
        }

        .result-lead {
          margin: 0;
          color: #cbd5e1;
          font-size: 0.95rem;
          line-height: 1.65;
          text-align: left;
        }

        .score-circle {
          position: relative;
          width: 164px;
          height: 164px;
          margin: 0 auto 1.2rem;
        }

        .progress-ring {
          width: 100%;
          height: 100%;
          transform: rotate(-90deg);
        }

        .ring-bg {
          fill: none;
          stroke: rgba(255, 255, 255, 0.05);
          stroke-width: 8;
        }

        .ring-fill {
          fill: none;
          stroke: url(#scoreGradient);
          stroke-width: 8;
          stroke-linecap: round;
        }

        .score-value {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }

        .score-value .number {
          font-size: 3rem;
          font-weight: 800;
          color: #f472b6;
        }

        .score-value .unit {
          font-size: 1.1rem;
          color: #f472b6;
          margin-left: 0.2rem;
        }

        .participants-bar {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          margin-bottom: 1rem;
          font-size: 0.98rem;
          font-weight: 600;
          color: #cbd5e1;
          flex-wrap: wrap;
        }

        .love-icon {
          font-size: 1rem;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .stat-box {
          background: rgba(255, 255, 255, 0.05);
          padding: 1rem 0.8rem;
          border-radius: 18px;
          border: 1px solid rgba(255, 255, 255, 0.05);
          text-align: left;
        }

        .stat-box label {
          font-size: 0.8rem;
          color: #94a3b8;
          display: block;
          margin-bottom: 0.5rem;
        }

        .stat-box .val {
          font-size: 1.1rem;
          font-weight: 800;
          color: white;
        }

        .guide-box,
        .deep-box {
          padding: 1rem;
          border-radius: 18px;
          margin-bottom: 1rem;
          text-align: left;
        }

        .guide-box {
          background: rgba(59, 130, 246, 0.08);
          border: 1px solid rgba(147, 197, 253, 0.35);
        }

        .deep-box {
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(148, 163, 184, 0.3);
        }

        .guide-box h3,
        .deep-box h3 {
          margin: 0 0 0.55rem;
          font-size: 0.95rem;
        }

        .guide-box h3 {
          color: #bfdbfe;
        }

        .deep-box h3 {
          color: #c4b5fd;
        }

        .guide-box p,
        .deep-box p {
          margin: 0.35rem 0;
          color: #e2e8f0;
          font-size: 0.92rem;
          line-height: 1.5;
        }

        .start-btn {
          width: 100%;
          min-height: var(--mobile-button-height);
          padding: 1rem;
          border-radius: 16px;
          border: none;
          font-weight: 700;
          cursor: pointer;
          margin-bottom: 0.75rem;
        }

        .start-btn.primary {
          background: linear-gradient(135deg, #ec4899, #8b5cf6);
          color: white;
        }

        .start-btn.secondary {
          background: rgba(255, 255, 255, 0.05);
          color: #cbd5e1;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        @media (max-width: 600px) {
          .stats-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <svg style={{ height: 0, width: 0, position: 'absolute' }}>
        <defs>
          <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ec4899" />
            <stop offset="100%" stopColor="#8b5cf6" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
};

export default HarmonyResult;
